import React, { FC, useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Textfield, TextfieldProps } from "@natera/form";
import { Tooltip } from "@natera/material/lib/tooltip";
import "./emailInputWithDomainSuggestions.scss";

const EMAIL_DOMAINS = [
  "gmail.com",
  "icloud.com",
  "outlook.com",
  "yahoo.com",
  "protonmail.com",
  "zoho.com",
  "gmx.com",
  "aol.com",
];

export interface EmailInputWithDomainSuggestionsProps extends Omit<
  TextfieldProps,
  "onChange" | "value" | "defaultValue" | "onFocus" | "onBlur"
> {
  value?: string;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  tooltipContent?: string;
  showTooltip?: boolean;
  error?: string;
}

export const EmailInputWithDomainSuggestions: FC<
  EmailInputWithDomainSuggestionsProps
> = ({
  value = "",
  onChange,
  onFocus,
  onBlur,
  tooltipContent,
  showTooltip = false,
  error,
  ...textfieldProps
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedDomainIndex, setSelectedDomainIndex] = useState(-1);
  const [domainFilter, setDomainFilter] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getFilteredDomains = () => {
    if (!domainFilter) {
      return EMAIL_DOMAINS;
    }
    const filterLower = domainFilter.toLowerCase();
    return EMAIL_DOMAINS.filter((domain) =>
      domain.toLowerCase().startsWith(filterLower)
    );
  };

  const filteredDomains = getFilteredDomains();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inContainer = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inContainer && !inDropdown) {
        setShowDropdown(false);
        setSelectedDomainIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (
      showDropdown &&
      selectedDomainIndex >= 0 &&
      dropdownRef.current &&
      filteredDomains.length > 0
    ) {
      const selectedElement = dropdownRef.current.querySelector(
        `#email-domain-${selectedDomainIndex}`
      ) as HTMLElement;

      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedDomainIndex, showDropdown, filteredDomains.length]);

  // iOS Safari: convert visual viewport coords to layout viewport for `fixed` positioning.
  const updateDropdownPosition = () => {
    if (!containerRef.current || !showDropdown) return;
    const emailInput = containerRef.current.querySelector("input");
    const dropdownAnchorRect = (
      emailInput ?? containerRef.current
    ).getBoundingClientRect();
    const visualViewport = window.visualViewport;
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent));
    const offsetX = isIOS && visualViewport ? visualViewport.offsetLeft : 0;
    const offsetY = isIOS && visualViewport ? visualViewport.offsetTop : 0;
    setDropdownPosition({
      top: dropdownAnchorRect.bottom + offsetY,
      left: dropdownAnchorRect.left + offsetX,
      width: dropdownAnchorRect.width,
    });
  };

  useLayoutEffect(() => {
    if (showDropdown && filteredDomains.length > 0) {
      updateDropdownPosition();
    } else {
      setDropdownPosition(null);
    }
  }, [showDropdown, filteredDomains.length]);

  const isValidDomainTyping = (domain: string) => {
    return (
      !domain.includes(" ") && !domain.includes(".") && !domain.includes("@")
    );
  };

  const hideDropdown = () => {
    setShowDropdown(false);
    setDomainFilter("");
  };

  const resetDropdown = () => {
    setShowDropdown(false);
    setSelectedDomainIndex(-1);
    setDomainFilter("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onChange?.(value);

    const atIndex = value.lastIndexOf("@");
    if (atIndex === -1) {
      hideDropdown();
      return;
    }
    const domainPart = value.slice(atIndex + 1);

    if (!isValidDomainTyping(domainPart)) {
      hideDropdown();
      return;
    }

    setShowDropdown(true);
    setSelectedDomainIndex(-1);
    setDomainFilter(domainPart);
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.();
    const value = e.target.value;
    const atIndex = value.lastIndexOf("@");

    if (atIndex === -1) {
      hideDropdown();
      return;
    }

    const domainPart = value.slice(atIndex + 1);

    if (!isValidDomainTyping(domainPart)) {
      hideDropdown();
      return;
    }

    setShowDropdown(true);
    setDomainFilter(domainPart);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.();

    const relatedTarget = e.relatedTarget;

    const isInteractingWithDropdown =
      relatedTarget instanceof HTMLElement &&
      (dropdownRef.current?.contains(relatedTarget) ||
        containerRef.current?.contains(relatedTarget));

    if (isInteractingWithDropdown) {
      return;
    }

    resetDropdown();
  };

  const handleDomainSelect = (domain: string) => {
    const atIndex = value.lastIndexOf("@");

    if (atIndex === -1) {
      resetDropdown();
      return;
    }

    const newValue = value.slice(0, atIndex + 1) + domain;
    onChange?.(newValue);

    resetDropdown();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        setSelectedDomainIndex((prev) =>
          prev < filteredDomains.length - 1 ? prev + 1 : 0
        );
        break;
      }

      case "ArrowUp": {
        e.preventDefault();
        setSelectedDomainIndex((prev) =>
          prev > 0 ? prev - 1 : filteredDomains.length - 1
        );
        break;
      }

      case "Enter": {
        if (selectedDomainIndex < 0) return;

        const selectedDomain = filteredDomains[selectedDomainIndex];
        if (!selectedDomain) return;

        e.preventDefault();
        handleDomainSelect(selectedDomain);
        break;
      }

      case "Escape": {
        e.preventDefault();
        resetDropdown();
        break;
      }

      default:
        break;
    }
  };

  const textfieldElement = (
    <Textfield
      {...textfieldProps}
      value={value}
      onChange={handleInputChange}
      onFocus={handleInputFocus}
      onBlur={handleInputBlur}
      onKeyDown={handleKeyDown}
      invalid={!!error}
      aria-invalid={!!error}
      id="email"
      role="combobox"
      aria-autocomplete="list"
      aria-controls="email-domain-listbox"
      aria-describedby={tooltipContent ? "email-tooltip" : undefined}
      aria-activedescendant={
        selectedDomainIndex >= 0
          ? `email-domain-${selectedDomainIndex}`
          : undefined
      }
      aria-expanded={showDropdown}
    />
  );

  return (
    <div ref={containerRef} className="email-input-with-domain-suggestions">
      {tooltipContent ? (
        <Tooltip
          content={tooltipContent}
          position={{ xPos: 2, yPos: 2 }}
          opened={showTooltip && !showDropdown}
          role="tooltip"
          id="email-tooltip"
        >
          {textfieldElement}
        </Tooltip>
      ) : (
        textfieldElement
      )}
      {showDropdown &&
        filteredDomains.length > 0 &&
        dropdownPosition &&
        createPortal(
          <div
            className="email-input-with-domain-suggestions__dropdown-container email-input-with-domain-suggestions__dropdown-container--portal"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            <div
              ref={dropdownRef}
              className="email-input-with-domain-suggestions__dropdown"
              role="listbox"
              id="email-domain-listbox"
            >
              {filteredDomains.map((domain, index) => (
                <div
                  key={domain}
                  role="option"
                  aria-selected={index === selectedDomainIndex}
                  id={`email-domain-${index}`}
                  className={`email-input-with-domain-suggestions__dropdown-item ${
                    index === selectedDomainIndex
                      ? "email-input-with-domain-suggestions__dropdown-item--selected"
                      : ""
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleDomainSelect(domain);
                  }}
                  onMouseEnter={() => setSelectedDomainIndex(index)}
                >
                  {domain}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default EmailInputWithDomainSuggestions;
